<template>
  <div
    ref="root"
    class="fcc"
    :class="{ 'fcc--dragging': dragging }"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div v-if="attachments.length" class="fcc-attachments">
      <div
        v-for="(a, i) in attachments"
        :key="a.fileName + '-' + i"
        class="fcc-chip"
      >
        <img
          v-if="a.fileType === 'image'"
          :src="a.url"
          class="fcc-chip-img"
          alt=""
        />
        <f7-icon
          v-else-if="isPdf(a)"
          f7="doc_on_doc"
          size="18"
          color="#C0440A"
        />
        <f7-icon
          v-else-if="a.fileType === 'audio'"
          f7="music_note"
          size="18"
          color="#1A6B42"
        />
        <f7-icon
          v-else-if="a.fileType === 'video'"
          f7="play_rectangle"
          size="18"
          color="#3B4FCA"
        />
        <f7-icon v-else f7="doc_text" size="18" color="#8e8e93" />
        <div class="fcc-chip-meta">
          <div class="fcc-chip-name">{{ a.fileName }}</div>
          <div class="fcc-chip-size">{{ fileSize(a.size) }}</div>
        </div>
        <f7-link
          class="fcc-chip-x"
          @click="removeAttachment(i)"
          icon-f7="xmark"
          icon-color="red"
          size="10"
          :aria-label="$t('feed.removeAttachment')"
        >
        </f7-link>
      </div>
    </div>

    <div class="fcc-row">
      <textarea
        ref="textareaEl"
        v-model="text"
        :placeholder="placeholder"
        rows="1"
        class="fcc-textarea"
        @input="onInput"
        @keydown.enter="onEnter"
        @paste="onPaste"
      />
      <f7-link
        v-if="showCancel && (text.trim() || attachments.length)"
        class="fcc-cancel"
        @click="$emit('cancel')"
        >{{ $t("common.cancel") }}</f7-link
      >

      <f7-link class="fcc-icon" :aria-label="$t('feed.emoji')" @click="toggleEmoji">
        <f7-icon f7="smiley" size="18" />
      </f7-link>
      <f7-link class="fcc-icon" :aria-label="$t('feed.attach')" @click="pickFiles">
        <f7-icon f7="paperclip" size="18" />
      </f7-link>
      <f7-link
        v-if="canSend"
        class="fcc-send"
        :aria-label="$t('feed.send')"
        @click="submit"
      >
        <f7-icon f7="paperplane_fill" color="primary" size="13" />
      </f7-link>
      <input
        ref="fileInput"
        type="file"
        multiple
        class="fcc-file"
        :accept="ACCEPT"
        @change="onFileChange"
      />
    </div>

    <div v-if="showEmoji" class="fcc-emoji" @click.stop>
      <Picker
        @select="onSelectEmoji"
        :data="emojiIndex"
        :set="getSet"
        :emojiSize="20"
        :showPreview="false"
        :showSkinTones="false"
        :perLine="8"
        color="var(--f7-theme-color)"
        :i18n="i18nLocale"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { f7 } from "framework7-vue";
import i18n from "@/js/i18n.js";
import data from "emoji-mart-vue-fast/data/all.json";
import "emoji-mart-vue-fast/css/emoji-mart.css";
import { Picker, EmojiIndex } from "emoji-mart-vue-fast/src";

const props = defineProps({
  placeholder: { type: String, default: "" },
  mentionName: { type: String, default: "" },
  showCancel: { type: Boolean, default: false },
  autoFocus: { type: Boolean, default: false },
});

const emit = defineEmits(["submit", "cancel"]);

const ACCEPT =
  "image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";
const MAX_FILES = 5;
const MAX_SIZE = 25 * 1024 * 1024;

const emojiIndex = new EmojiIndex(data, {
  custom: [
    {
      name: "avocash",
      short_names: ["avocash"],
      keywords: ["avocash"],
      text: "",
      emot: [],
      imageUrl: "/24x24.svg",
    },
  ],
});
const getSet = ref("native");
const i18nLocale = {
  search: i18n.global.t("emoji.search"),
  notfound: i18n.global.t("emoji.notfound"),
  categories: {
    search: i18n.global.t("emoji.searchResults"),
    recent: i18n.global.t("emoji.recent"),
    smileys: i18n.global.t("emoji.smileys"),
    people: i18n.global.t("emoji.people"),
    nature: i18n.global.t("emoji.nature"),
    foods: i18n.global.t("emoji.foods"),
    activity: i18n.global.t("emoji.activity"),
    places: i18n.global.t("emoji.places"),
    objects: i18n.global.t("emoji.objects"),
    symbols: i18n.global.t("emoji.symbols"),
    flags: i18n.global.t("emoji.flags"),
    custom: i18n.global.t("emoji.custom"),
  },
};

const root = ref(null);
const textareaEl = ref(null);
const fileInput = ref(null);
const text = ref(props.mentionName ? `@${props.mentionName} ` : "");
const attachments = ref([]);
const showEmoji = ref(false);
const dragging = ref(false);

const canSend = computed(
  () => text.value.trim() !== "" || attachments.value.length > 0,
);

function fileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function isPdf(a) {
  return (a.contentType || "").includes("pdf");
}

function resize(ta) {
  ta.style.height = "auto";
  ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
}

function onInput(e) {
  resize(e.target);
}

function onEnter(e) {
  if (e.shiftKey) return;
  e.preventDefault();
  submit();
}

function toggleEmoji() {
  showEmoji.value = !showEmoji.value;
}

function onSelectEmoji(emoji) {
  const ta = textareaEl.value;
  const start = ta ? ta.selectionStart ?? text.value.length : text.value.length;
  const end = ta ? ta.selectionEnd ?? text.value.length : text.value.length;
  text.value =
    text.value.slice(0, start) + emoji.native + text.value.slice(end);
  nextTick(() => {
    if (ta) {
      const pos = start + emoji.native.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
      resize(ta);
    }
  });
}

function pickFiles() {
  fileInput.value?.click();
}

function onFileChange() {
  if (fileInput.value) {
    addFiles(fileInput.value.files);
    fileInput.value.value = null;
  }
}

function addFiles(fileList) {
  const files = Array.from(fileList || []);
  for (const file of files) {
    if (attachments.value.length >= MAX_FILES) {
      f7.toast.show({
        text: i18n.global.t('feed.maxAttachments', { count: MAX_FILES }),
        position: "center",
        closeTimeout: 1500,
      });
      break;
    }
    if (file.size > MAX_SIZE) {
      f7.toast.show({
        text: i18n.global.t('feed.fileExceedsSize', { name: file.name }),
        position: "center",
        closeTimeout: 1500,
      });
      continue;
    }
    const contentType = file.type || "application/octet-stream";
    attachments.value.push({
      fileName: file.name,
      contentType,
      fileType: contentType.split("/")[0],
      size: file.size,
      url: URL.createObjectURL(file),
      file,
    });
  }
}

function removeAttachment(index) {
  const a = attachments.value[index];
  if (!a) return;
  window.URL.revokeObjectURL(a.url);
  attachments.value.splice(index, 1);
}

function onPaste(e) {
  const data = e.clipboardData || e.originalEvent?.clipboardData;
  const items = data?.items;
  const files = [];
  if (items) {
    for (const item of Array.from(items)) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
  }
  if (files.length > 0) {
    e.preventDefault();
    addFiles(files);
  }
}

function onDragOver() {
  dragging.value = true;
}

function onDragLeave(e) {
  if (
    !e.relatedTarget ||
    !root.value ||
    !root.value.contains(e.relatedTarget)
  ) {
    dragging.value = false;
  }
}

function onDrop(e) {
  dragging.value = false;
  addFiles(e.dataTransfer?.files);
}

function submit() {
  if (!canSend.value) return;
  const payload = {
    text: text.value.trim(),
    attachments: [...attachments.value],
  };
  emit("submit", payload);
  text.value = "";
  attachments.value.forEach((a) => window.URL.revokeObjectURL(a.url));
  attachments.value = [];
  showEmoji.value = false;
  nextTick(() => {
    if (textareaEl.value) resize(textareaEl.value);
  });
}

function onDocMouseDown(e) {
  if (showEmoji.value && root.value && !root.value.contains(e.target)) {
    showEmoji.value = false;
  }
}
watch(showEmoji, (val) => {
  if (val) document.addEventListener("mousedown", onDocMouseDown);
  else document.removeEventListener("mousedown", onDocMouseDown);
});

onMounted(() => {
  nextTick(() => {
    if (textareaEl.value) {
      resize(textareaEl.value);
      if (props.autoFocus) textareaEl.value.focus();
    }
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocMouseDown);
  attachments.value.forEach((a) => window.URL.revokeObjectURL(a.url));
});
</script>

<style scoped>
.fcc {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fcc-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}
.fcc-row .fcc-textarea {
  flex: 1;
  min-width: 0;
}
.fcc-textarea {
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 18px;
  padding: 7px 14px;
  font-size: 11px;
  line-height: 1.4;
  outline: none;
  background: var(--f7-list-bg-color, #f5f5f5);
  resize: none;
  overflow: hidden;
  min-height: 32px;
  max-height: 120px;
  box-sizing: border-box;
  font-family: inherit;
  color: var(--f7-text-color, #000);
}
.fcc-textarea:focus {
  border-color: var(--avocash-green, #1a6b42);
}
.fcc--dragging .fcc-row .fcc-textarea {
  border-color: var(--avocash-green, #1a6b42);
  border-style: dashed;
}
.fcc-icon {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--f7-on-surface-variant, #8e8e93);
  cursor: pointer;
  padding: 0;
}
.fcc-send {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  /* background: var(--avocash-green, #1A6B42); */
  /* color: #fff; */
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.fcc-cancel {
  background: none;
  border: none;
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
  cursor: pointer;
  padding: 0 2px;
  white-space: nowrap;
}
.fcc-file {
  display: none;
}
.fcc-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.fcc-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--f7-list-bg-color, #f5f5f5);
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 10px;
  padding: 4px 6px;
  max-width: 100%;
}
.fcc-chip-img {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  object-fit: cover;
  flex-shrink: 0;
}
.fcc-chip-meta {
  min-width: 0;
}
.fcc-chip-name {
  font-size: 10px;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fcc-chip-size {
  font-size: 9px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.fcc-chip-x {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--f7-on-surface-variant, #8e8e93);
  display: flex;
  flex-shrink: 0;
}
.fcc-emoji {
  position: absolute;
  bottom: 40px;
  right: 0;
  z-index: 30;
  background: var(--f7-page-bg-color, #fff);
  border-radius: 12px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.15);
  padding: 6px;
  width: 300px;
  max-width: calc(100vw - 24px);
}
.fcc-emoji :deep(.emoji-mart) {
  width: 100% !important;
  max-width: 288px;
}
</style>
